import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import {
  CONTACTS_PARTNERS_GET_CONTACT_TITLES_OPTIONS_ENDPOINT,
  CURRENCY_ENDPOINTS,
  FINANCE_GET_FISCAL_POSITIONS_OPTIONS_ENDPOINT,
  FINANCE_GET_PAYMENT_TERMS_OPTIONS_ENDPOINT,
  FINANCE_GET_TAXES_OPTIONS_ENDPOINT,
  HR_GET_EMPLOYMENT_RECORDS_OPTIONS_ENDPOINT,
  PRICING_GET_PRICELISTS_OPTIONS_ENDPOINT,
} from '../api/api-endpoints';
import { CurrencyService } from './currency.service';
import { RestApiService } from '../api/rest-api.service';
import { AutocompleteOption } from '../../shared/inputs/autocomplete-select/autocomplete-select.component';

export interface PartnerReferenceOption extends AutocompleteOption {
  code?: string;
  symbol?: string;
  is_base?: boolean;
}

export type PartnerReferenceKind =
  | 'currencies'
  | 'payment_terms'
  | 'pricelists'
  | 'taxes'
  | 'fiscal_positions'
  | 'employments'
  | 'contact_titles';

@Injectable({
  providedIn: 'root',
})
export class PartnerReferenceOptions {
  private readonly requests = new Map<string, Promise<PartnerReferenceOption[]>>();

  constructor(private readonly api: RestApiService, private readonly currency: CurrencyService) {}

  options(kind: PartnerReferenceKind, query = ''): Promise<PartnerReferenceOption[]> {
    if (kind === 'currencies') return this.currency.options(query);
    const endpoint = this.endpoint(kind);
    const search = query.trim();
    const cacheKey = `${endpoint}:${search.toLocaleLowerCase()}`;
    const cached = this.requests.get(cacheKey);
    if (cached) return cached.then((options) => options.map((option) => ({ ...option })));

    const params = new URLSearchParams({ limit: '100' });
    if (search) params.set('query', search);
    const request = firstValueFrom(
      this.api.get<{ options?: Array<Record<string, unknown>> }>(`${endpoint}?${params.toString()}`),
    ).then((response) => (response.options ?? []).map((row) => ({
      value: String(row['value'] ?? row['id'] ?? ''),
      label: String(row['label'] ?? row['name'] ?? row['code'] ?? ''),
      code: row['code'] == null ? undefined : String(row['code']),
      symbol: row['symbol'] == null ? undefined : String(row['symbol']),
      is_base: row['is_base'] === true,
    })).filter((option) => option.value && option.label)).catch((error) => {
      this.requests.delete(cacheKey);
      throw error;
    });
    this.requests.set(cacheKey, request);
    return request.then((options) => options.map((option) => ({ ...option })));
  }

  preload(kind: PartnerReferenceKind, values: string[]): Promise<PartnerReferenceOption[]> {
    if (kind === 'currencies') return this.currency.preload(values);
    const requested = new Set(values.filter(Boolean));
    return this.options(kind).then((options) => options.filter((option) => requested.has(option.value)));
  }

  private endpoint(kind: PartnerReferenceKind): string {
    return {
      currencies: CURRENCY_ENDPOINTS.options,
      payment_terms: FINANCE_GET_PAYMENT_TERMS_OPTIONS_ENDPOINT,
      pricelists: PRICING_GET_PRICELISTS_OPTIONS_ENDPOINT,
      taxes: FINANCE_GET_TAXES_OPTIONS_ENDPOINT,
      fiscal_positions: FINANCE_GET_FISCAL_POSITIONS_OPTIONS_ENDPOINT,
      employments: HR_GET_EMPLOYMENT_RECORDS_OPTIONS_ENDPOINT,
      contact_titles: CONTACTS_PARTNERS_GET_CONTACT_TITLES_OPTIONS_ENDPOINT,
    }[kind];
  }
}
